// AccessConsole — the access-control admin console at /admin/access
// (PLEX-4095, spec 2026-06-26-v2-access-control-design.md).
//
// Left: every program and project in the cell (rd's GET
// /api/admin/access/resources), programs with their projects nested.
//
// Resource lens (default): the selected resource's access list on the right,
// one row per person with why they have access (owner / program member /
// via <program> / project grant), plus grant and revoke through the existing
// /programs|projects/{id}/access endpoints.
//
// Person lens ("Filter by user"): rd's GET /api/admin/access/users/{email}
// badges every tree node with that person's access, "no access" included, and
// the right pane lists their access with bulk revoke and a "Grant to…" picker
// (PersonPanel). Clicking a node they reach toggles it for revoke; clicking one
// they can't reach picks it to grant.
//
// Chats aren't in the tree yet (a question for Tim on PLEX-4095).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { inject, observer } from "mobx-react";

import { fetchAccessResources, fetchUserAccess, grantAccess, revokeAccess } from "../../queries/adminAccessApi";
import {
  accessRows, buildTree, filterTree, findResource, looksLikeEmail, NO_ACCESS,
  OWNER, personBadge, personIndex, personRows, PROGRAM_MEMBER, PROJECT_GRANT, resourceKey, VIA_PROGRAM,
} from "./accessModel";
import PersonPanel from "./PersonPanel";
import "./AdminAccess.css";

const CHIP = {
  [OWNER]: "brand",
  [PROGRAM_MEMBER]: "good",
  [VIA_PROGRAM]: "",
  [PROJECT_GRANT]: "premium",
  own_chat: "",
  [NO_ACCESS]: "adminAcc-chip--none",
};

const chipClass = (badge) => CHIP[badge] || "";

// "program:<id>" → {kind, id}. Ids never contain ":" (uuids, or slugs from before PLEX-6051).
const parseKey = (key) => {
  const at = (key || "").indexOf(":");
  return at < 0 ? null : { kind: key.slice(0, at), id: key.slice(at + 1) };
};

const errorText = (e) => e?.message || String(e);

// ─── Tree ─────────────────────────────────────────────────────────────────────

const Node = ({ resource, kind, selected, onSelect, meta, badge }) => (
  <button
    type="button"
    className={`adminAcc-node adminAcc-node--${kind}${selected ? " is-selected" : ""}`
      + `${badge?.provenance === NO_ACCESS ? " is-unreachable" : ""}`}
    aria-pressed={selected}
    onClick={() => onSelect(kind, resource.id)}
  >
    <span className="adminAcc-node__name">{resource.name || resource.id}</span>
    {badge
      ? <span className={`chip adminAcc-node__badge ${chipClass(badge.chip)}`}>{badge.label}</span>
      : meta != null && <span className="adminAcc-node__meta">{meta}</span>}
  </button>
);

const memberCount = (program) => {
  const n = (program.members || []).length;
  return n ? `${n} member${n === 1 ? "" : "s"}` : null;
};

// `isSelected(kind, id)` and `badgeFor(kind, id)` let the two lenses share the
// tree: the resource lens selects one node, the person lens badges them all.
const Tree = ({ tree, isSelected, onSelect, badgeFor }) => {
  if (tree.programs.length === 0 && tree.unfiled.length === 0) {
    return <div className="adminAcc-tree__empty">Nothing matches.</div>;
  }
  const node = (resource, kind, meta) => (
    <Node resource={resource} kind={kind} selected={isSelected(kind, resource.id)} onSelect={onSelect}
          meta={meta} badge={badgeFor?.(kind, resource.id)} />
  );
  return (
    <ul className="adminAcc-tree__list">
      {tree.programs.map(({ program, projects }) => (
        <li key={program.id}>
          {node(program, "program", memberCount(program))}
          {projects.length > 0 && (
            <ul>
              {projects.map((p) => <li key={p.id}>{node(p, "project")}</li>)}
            </ul>
          )}
        </li>
      ))}
      {tree.unfiled.length > 0 && (
        <li className="adminAcc-tree__unfiled">
          <div className="adminAcc-tree__group">Projects outside a listed program</div>
          <ul>
            {tree.unfiled.map((p) => <li key={p.id}>{node(p, "project")}</li>)}
          </ul>
        </li>
      )}
    </ul>
  );
};

const PersonFilter = ({ person, onShow, onClear, busy }) => {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState(null);
  if (person) {
    return (
      <div className="adminAcc-personChip">
        <span>Viewing as <strong>{person.email}</strong></span>
        <button type="button" className="btn small ghost adminAcc-clearPerson" onClick={onClear}
                disabled={busy}>Clear</button>
      </div>
    );
  }
  const submit = (e) => {
    e.preventDefault();
    if (!looksLikeEmail(email)) {
      setErr("Enter an email address.");
      return;
    }
    setErr(null);
    onShow(email.trim().toLowerCase());
    setEmail("");
  };
  return (
    <form className="adminAcc-personFilter" onSubmit={submit}>
      <input type="email" className="adminAcc-input" placeholder="Filter by user (email)"
             value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
      <button type="submit" className="btn small" disabled={busy}>Show</button>
      {err && <div className="err adminAcc-personFilter__err">{err}</div>}
    </form>
  );
};

// ─── Detail ───────────────────────────────────────────────────────────────────

const GrantForm = ({ kind, onGrant, busy }) => {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!looksLikeEmail(email)) {
      setErr("Enter an email address.");
      return;
    }
    try {
      await onGrant(email.trim().toLowerCase());
      setEmail("");
    } catch (ex) {
      setErr(errorText(ex));
    }
  };
  return (
    <form className="adminAcc-grant" onSubmit={submit}>
      <label className="adminAcc-grant__label">
        <span>
          {kind === "program"
            ? "Grant access to this program and all its projects"
            : "Grant access to this project only"}
        </span>
        <input
          type="email"
          className="adminAcc-input"
          placeholder="name@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />
      </label>
      <button type="submit" className="btn primary" disabled={busy}>
        {busy ? "Saving…" : "Grant"}
      </button>
      {err && <div className="err adminAcc-grant__err">{err}</div>}
    </form>
  );
};

const Detail = ({ resources, selection, onSelect, onGrant, onRevoke, busy }) => {
  const resource = selection && findResource(resources, selection.kind, selection.id);
  if (!resource) {
    return <div className="empty">Pick a program or project to see who can reach it.</div>;
  }
  const { kind } = selection;
  const program = kind === "project" ? findResource(resources, "program", resource.programId) : null;
  const rows = accessRows(resources, kind, resource.id);
  const revokeTitle = (row) => (kind === "program"
    ? `Revoke ${row.email}'s access to this program and its projects`
    : `Revoke ${row.email}'s project grant`);

  return (
    <div className="adminAcc-detail">
      <div className="adminEnt-eyebrow">{kind === "program" ? "Program" : "Project"}</div>
      <h2 className="serif">{resource.name || resource.id}</h2>
      <p className="adminAcc-detail__meta">
        Owned by <strong>{resource.ownerEmail}</strong>
        {kind === "program" && <> · {resource.projectCount || 0} projects</>}
        {kind === "project" && (
          <>
            {" · in "}
            {program
              ? <button type="button" className="adminAcc-link" onClick={() => onSelect("program", program.id)}>
                  {program.name || program.id}
                </button>
              : <code>{resource.programId}</code>}
          </>
        )}
      </p>

      <table className="adminAcc-table">
        <thead>
          <tr><th>Person</th><th>Access</th><th>Role</th><th aria-label="Actions" /></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.email} data-email={row.email}>
              <td>{row.email}</td>
              <td>
                <span className={`chip ${CHIP[row.badge] || ""}`}>{row.label}</span>
                {row.alsoGrant && <span className="chip premium adminAcc-also">{row.alsoGrant}</span>}
              </td>
              <td className="adminAcc-role">{row.role || "—"}</td>
              <td className="adminAcc-actions">
                {row.revocable && (
                  <button type="button" className="btn small ghost adminAcc-revoke" disabled={busy}
                          title={revokeTitle(row)} onClick={() => onRevoke(kind, resource.id, row.email)}>
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {kind === "project" && program && (
        <p className="adminAcc-note">
          People shown via {program.name || program.id} reach every project in it. Manage them on the program.
        </p>
      )}

      <GrantForm key={`${kind}:${resource.id}`} kind={kind} busy={busy}
                 onGrant={(email) => onGrant(kind, resource.id, email)} />
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const AccessConsole = ({ userStore }) => {
  const authData = userStore?.authData;
  const [resources, setResources] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  // Person lens: {email, access, loading, error}, the grants ticked for revoke,
  // and the "Grant to…" choice ("program:<id>" / "project:<id>").
  const [person, setPerson] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [grantTarget, setGrantTarget] = useState(null);

  const load = useCallback(() => fetchAccessResources(authData)
    .then((r) => { setResources(r || { programs: [], projects: [] }); setError(null); })
    .catch((e) => setError(errorText(e))), [authData]);

  useEffect(() => { load(); }, [load]);

  // Re-read a person's access without blanking the pane (after a grant or revoke).
  // The person on screen. A lookup that comes back for anyone else (the admin
  // cleared them, or moved on to someone new, while it was in flight) is
  // dropped rather than painted over the current view.
  const shownEmail = useRef(null);
  const refreshPerson = useCallback((email) => fetchUserAccess(authData, email)
    .then((access) => {
      if (shownEmail.current === email) setPerson({ email, access, loading: false, error: null });
    })
    .catch((e) => {
      if (shownEmail.current === email) setPerson({ email, access: null, loading: false, error: errorText(e) });
    }), [authData]);

  const showPerson = (email) => {
    shownEmail.current = email;
    setPerson({ email, access: null, loading: true, error: null });
    setChecked(new Set());
    setGrantTarget(null);
    setNotice(null);
    return refreshPerson(email);
  };

  const clearPerson = () => {
    shownEmail.current = null;
    setPerson(null);
    setChecked(new Set());
    setGrantTarget(null);
    setNotice(null);
  };

  const tree = useMemo(() => filterTree(buildTree(resources), query), [resources, query]);
  const index = useMemo(() => personIndex(person?.access), [person]);
  const rows = useMemo(() => (person?.access ? personRows(resources, person.access) : []), [resources, person]);

  const select = (kind, id) => { setSelection({ kind, id }); setNotice(null); };

  const toggle = (key) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // In the person lens a node they can't reach is picked to grant; one of
  // their own grants is ticked for revoke. Other access isn't revocable here.
  const personSelect = (kind, id) => {
    const key = resourceKey(kind, id);
    if (!index.has(key)) {
      setGrantTarget(key);
      return;
    }
    if (rows.find((r) => r.key === key)?.revocable) toggle(key);
  };

  const handleGrant = async (kind, id, email) => {
    setBusy(true);
    setNotice(null);
    try {
      await grantAccess(authData, kind, id, email);
      await load();
      setNotice(`Granted ${email} access.`);
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (kind, id, email) => {
    const what = kind === "program" ? "this program and all its projects" : "this project";
    if (!window.confirm(`Revoke ${email}'s access to ${what}?`)) return;
    setBusy(true);
    setNotice(null);
    try {
      await revokeAccess(authData, kind, id, email);
      await load();
      setNotice(`Revoked ${email}'s access.`);
    } catch (e) {
      setNotice(null);
      setError(`Couldn't revoke ${email}: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  };

  // One revoke per ticked grant, in turn. Failures are reported by name and stay ticked.
  const handleRevokeSelected = async () => {
    const { email } = person;
    const chosen = rows.filter((r) => r.revocable && checked.has(r.key));
    if (chosen.length === 0) return;
    const what = chosen.length === 1 ? "1 program or project" : `${chosen.length} programs and projects`;
    if (!window.confirm(`Revoke ${email}'s access to ${what}?`
        + "\nRevoking a program also removes the access it gave to its projects.")) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    const failed = [];
    for (const row of chosen) {
      try {
        await revokeAccess(authData, row.kind, row.id, email);
      } catch (e) {
        failed.push({ row, message: errorText(e) });
      }
    }
    setChecked(new Set(failed.map((f) => f.row.key)));
    await load();
    await refreshPerson(email);
    const done = chosen.length - failed.length;
    if (done) setNotice(`Revoked ${plural(done, "grant")} from ${email}.`);
    if (failed.length) {
      setError(`Couldn't revoke ${failed.map((f) => `${f.row.name || f.row.id} (${f.message})`).join("; ")}`);
    }
    setBusy(false);
  };

  const handleGrantTo = async () => {
    const target = parseKey(grantTarget);
    if (!target) return;
    const { email } = person;
    const name = findResource(resources, target.kind, target.id)?.name || target.id;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await grantAccess(authData, target.kind, target.id, email);
      setGrantTarget(null);
      await load();
      await refreshPerson(email);
      setNotice(`Granted ${email} access to ${name}.`);
    } catch (e) {
      setError(`Couldn't grant ${email} access to ${name}: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const personLens = !!person;
  const isSelected = personLens
    ? (kind, id) => checked.has(resourceKey(kind, id)) || grantTarget === resourceKey(kind, id)
    : (kind, id) => selection?.kind === kind && selection?.id === id;
  const badgeFor = person?.access
    ? (kind, id) => personBadge(resources, person.access, kind, id, index)
    : undefined;

  return (
    <div className="adminAcc">
      <div className="page-hero">
        <div>
          <div className="adminEnt-eyebrow">Access control{resources?.cell ? ` · ${resources.cell}` : ""}</div>
          <h1 className="serif">Who can <em>reach</em> what</h1>
          <p className="lede">
            {personLens
              ? "Everything this person can reach and why. Tick their grants to revoke them, or pick something they can't reach to grant it."
              : "Every program and project in this cell. Pick one to see who has access and why, and to grant or revoke it. Or filter by a user to see everything they can reach."}
          </p>
        </div>
      </div>

      {error && <div className="err" role="alert">{error}</div>}
      {notice && <div className="adminAcc-notice" role="status">{notice}</div>}

      {resources === null ? (
        !error && <div className="loading">Loading programs and projects…</div>
      ) : (
        <div className="adminAcc-grid">
          <aside className="adminAcc-tree" aria-label="Programs and projects">
            <PersonFilter person={person} onShow={showPerson} onClear={clearPerson} busy={busy} />
            <input
              type="search"
              className="adminAcc-input adminAcc-search"
              placeholder="Filter programs and projects"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Tree tree={tree} isSelected={isSelected} onSelect={personLens ? personSelect : select}
                  badgeFor={badgeFor} />
          </aside>
          <section className="adminAcc-pane">
            {personLens ? (
              <PersonPanel resources={resources} person={person} checked={checked} onToggle={toggle}
                           onRevokeSelected={handleRevokeSelected} grantTarget={grantTarget}
                           onGrantTargetChange={setGrantTarget} onGrant={handleGrantTo} busy={busy}
                           chipClass={chipClass} />
            ) : (
              <Detail resources={resources} selection={selection} onSelect={select}
                      onGrant={handleGrant} onRevoke={handleRevoke} busy={busy} />
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default inject("userStore")(observer(AccessConsole));
